import { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authApi, billingApi } from '../services/api';
import { Loader2, ChefHat } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarded?: boolean;
  requireSubscription?: boolean;
}

export default function ProtectedRoute({
  children,
  requireOnboarded = true,
  requireSubscription = true
}: ProtectedRouteProps) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const lastCheckRef = useRef<number>(0);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('manager_token');

      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      // Throttle auth checks to prevent rapid re-fetching (min 2 seconds between checks)
      const now = Date.now();
      if (now - lastCheckRef.current < 2000 && !isLoading) {
        return;
      }
      lastCheckRef.current = now;

      try {
        // Verify token is still valid
        const response = await authApi.me();
        const data = response.data.data;

        setIsAuthenticated(true);

        // Check if restaurant needs onboarding
        if (data.restaurants && data.restaurants.length > 0) {
          const currentRestaurant = data.restaurants[0];
          setNeedsOnboarding(currentRestaurant.status === 'onboarding');

          // Subscription access: ask the SERVER — it is the single source of truth
          // for trial length, per-account extensions, and read-only grace. A local
          // re-computation here (the old hard-coded 14 days) kept locking people out
          // even when the server said they had access.
          try {
            const access = (await billingApi.getAccess()).data?.data || {};
            const inGrace = access.status === 'trial_expired' && (access.graceDaysLeft ?? 0) > 0;
            setNeedsSubscription(!(access.hasAccess || inGrace));
          } catch {
            // Can't reach the access check — don't lock the door on a network blip;
            // the API still enforces subscription on every write.
            setNeedsSubscription(false);
          }
        } else {
          setNeedsOnboarding(false);
          setNeedsSubscription(true);
        }
      } catch (error) {
        // Token is invalid, clear it
        localStorage.removeItem('manager_token');
        localStorage.removeItem('manager_refresh_token');
        localStorage.removeItem('manager_user');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [location.pathname]); // Re-check when navigating to a new page

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <ChefHat className="w-12 h-12 text-primary-600 mx-auto mb-4 animate-pulse" />
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login, preserving the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user needs onboarding and we require onboarded
  if (requireOnboarded && needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // If user needs subscription and we require it (and not already on billing page)
  if (requireSubscription && needsSubscription && location.pathname !== '/subscribe') {
    return <Navigate to="/subscribe" replace />;
  }

  return <>{children}</>;
}
